// Who the signed-in person is and what they may see. PURE logic (no network) so
// it is unit-tested.
//
// IMPORTANT: this decides what to SHOW, never what is ALLOWED. The database
// (row-level security) is the only authorization boundary: a hand-built request
// that skips this code gets exactly the same answers. Getting a role wrong here
// can make a screen look empty; it can never expose data.

export type AppRole = 'admin' | 'auditor' | 'dept_treasurer' | 'dept_viewer'

export interface RoleGrant {
  id: string
  role: AppRole
  departmentId: string | null
}

export interface AppUserRow {
  id: string
  full_name: string
  email: string
  is_active: boolean
  must_change_password: boolean
}

export interface RoleRow {
  id: string
  role: AppRole
  department_id: string | null
}

export interface Profile {
  id: string
  fullName: string
  email: string
  isActive: boolean
  mustChangePassword: boolean
  roles: RoleGrant[]
  /** Church-wide administrator: approvals, reconciliation, settings, accounts. */
  isAdmin: boolean
  /** Church-wide read-only. */
  isAuditor: boolean
  /** Admin or auditor: may look at every department. */
  isChurchReader: boolean
  /** Departments the person is a treasurer of (may record things). */
  writeDeptIds: string[]
  /** Departments the person may look at (treasurer or viewer). */
  readDeptIds: string[]
  /** The one owner of the app: the only person who can permanently delete records. */
  isOwner: boolean
}

const unique = <T,>(xs: T[]): T[] => [...new Set(xs)]

export function buildProfile(user: AppUserRow, roleRows: RoleRow[], isOwner = false): Profile {
  const roles: RoleGrant[] = roleRows.map((r) => ({
    id: r.id,
    role: r.role,
    departmentId: r.department_id,
  }))
  const isAdmin = roles.some((r) => r.role === 'admin')
  const isAuditor = roles.some((r) => r.role === 'auditor')
  const deptIds = (...wanted: AppRole[]) =>
    unique(
      roles
        .filter((r) => wanted.includes(r.role) && r.departmentId !== null)
        .map((r) => r.departmentId as string),
    ).sort()
  return {
    id: user.id,
    fullName: user.full_name,
    email: user.email,
    isActive: user.is_active,
    mustChangePassword: user.must_change_password,
    roles,
    isAdmin,
    isAuditor,
    isChurchReader: isAdmin || isAuditor,
    writeDeptIds: deptIds('dept_treasurer'),
    readDeptIds: deptIds('dept_treasurer', 'dept_viewer'),
    isOwner,
  }
}

export type Home =
  | { to: '/admin' }
  | { to: '/d/$deptId'; params: { deptId: string } }

/** Where someone lands after signing in. null = nothing assigned yet. */
export function homeFor(p: Profile): Home | null {
  if (p.isChurchReader) return { to: '/admin' }
  const first = p.readDeptIds[0]
  if (first) return { to: '/d/$deptId', params: { deptId: first } }
  return null
}

/** May this person open this department's screens? (UI convenience only.) */
export function canOpenDepartment(p: Profile, deptId: string): boolean {
  return p.isChurchReader || p.readDeptIds.includes(deptId)
}

/** May this person RECORD things for this department? (UI convenience only.) */
export function canWriteDepartment(p: Profile, deptId: string): boolean {
  return p.isAdmin || p.writeDeptIds.includes(deptId)
}

const ROLE_NAMES: Record<AppRole, string> = {
  admin: 'Administrator',
  auditor: 'Auditor (read-only)',
  dept_treasurer: 'Department treasurer',
  dept_viewer: 'Department viewer (read-only)',
}

/** e.g. "Administrator" or "Department treasurer, Youth Ministry". */
export function describeRoles(p: Profile, deptName: (id: string) => string | undefined): string[] {
  return p.roles.map((r) => {
    const base = ROLE_NAMES[r.role]
    if (!r.departmentId) return base
    const name = deptName(r.departmentId)
    return name ? `${base}, ${name}` : base
  })
}

/** Why a signed-in person cannot use the app yet (null = they can). */
export type NoAccessReason = 'no-record' | 'inactive' | 'no-role'

export function noAccessReason(p: Profile | null): NoAccessReason | null {
  if (p === null) return 'no-record'
  if (!p.isActive) return 'inactive'
  if (homeFor(p) === null) return 'no-role'
  return null
}
